import type { PropertyRoomTypeWithImages } from '../ImagesVideo/types';

import type { OfferBuilderRoom } from './types';

export { getRoomKey } from '../utils';

export const REQUIRED_ROOM_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
  { key: 'bedConfiguration', label: 'Bed configuration' },
  { key: 'category', label: 'Room category' },
] as const;

export const BED_TYPE_OPTIONS = [
  'KingBed',
  'QueenBed',
  'DoubleBed',
  'SingleBed',
  'TwinBed',
  'SofaBed',
  'BunkBed',
  'RollAwayBed',
  'DayBed',
  'DoubleSofaBed',
  'QueenSofaBed',
  'Other',
];

export type RequiredRoomField = (typeof REQUIRED_ROOM_FIELDS)[number];

export function getRoomImageCount(room: PropertyRoomTypeWithImages) {
  return (room.images ?? []).filter((image) => image.id_cloudinary_external).length;
}

export function getMissingRequiredRoomFields(room: OfferBuilderRoom): Array<RequiredRoomField> {
  return REQUIRED_ROOM_FIELDS.filter((field) => {
    switch (field.key) {
      case 'name':
        return !room.name?.trim();
      case 'description':
        return !room.description?.trim();
      case 'bedConfiguration':
        return !hasConfiguredBedType(room);
      case 'category':
        return !room.category?.trim();
      default:
        return false;
    }
  });
}

export function getRoomSummary(room: OfferBuilderRoom) {
  const summaryParts = [
    formatBedSummary(room),
    typeof room.size_sqm === 'number' && room.size_sqm > 0 ? `${room.size_sqm}m²` : undefined,
  ].filter(Boolean);

  return summaryParts.length ? summaryParts.join(' · ') : 'Room details missing';
}

export function formatBedType(type?: string) {
  return (type ?? '')
    .replace(/Bed$/, '')
    .replace('DoubleSofa', 'Double Sofa')
    .replace('QueenSofa', 'Queen Sofa')
    .replace('RollAway', 'Roll Away')
    .trim();
}

function hasConfiguredBedType(room: OfferBuilderRoom) {
  if (!room.bed_groups?.length) {
    return true;
  }

  return room.bed_groups.some((group) => group.configuration?.some((configuration) => configuration.type?.trim()));
}

function formatBedConfiguration(configuration: { quantity?: number; size?: string; type?: string }) {
  if (!configuration.quantity) {
    return undefined;
  }

  const bedType = formatBedType(configuration.type);
  const bedSize = formatBedType(configuration.size);
  const bedLabel = bedType || bedSize;

  return bedLabel ? `${configuration.quantity} ${bedLabel} Bed${configuration.quantity > 1 ? 's' : ''}` : undefined;
}

function formatBedSummary(room: OfferBuilderRoom) {
  const bedConfigurations = room.bed_groups?.flatMap((group) => group.configuration ?? []) ?? [];
  const bedConfigurationSummaries = bedConfigurations.map(formatBedConfiguration).filter(Boolean);

  if (bedConfigurationSummaries.length > 0) {
    const lastBedConfigurationSummary = bedConfigurationSummaries[bedConfigurationSummaries.length - 1];
    const leadingBedConfigurationSummaries = bedConfigurationSummaries.slice(0, -1);

    return leadingBedConfigurationSummaries.length
      ? `${leadingBedConfigurationSummaries.join(', ')} and ${lastBedConfigurationSummary}`
      : lastBedConfigurationSummary;
  }

  if (typeof room.number_of_beds === 'number') {
    return `${room.number_of_beds} ${room.number_of_beds === 1 ? 'bed' : 'beds'}`;
  }

  if (Array.isArray(room.number_of_beds) && room.number_of_beds.length > 0) {
    const bedCount = room.number_of_beds.reduce((total, count) => total + count, 0);
    return `${bedCount} ${bedCount === 1 ? 'bed' : 'beds'}`;
  }

  return undefined;
}
